import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ShoppingBag, ArrowLeft, MapPin } from 'lucide-react';

const CAT_EMOJI = { 'מחבטים': '🎾', 'נעליים': '👟', 'תיקים': '🎒', 'גידים למחבט': '🧵', 'ביגוד': '👕', 'אחר': '📦' };

export const GearShowcase = () => {
  const { API } = useContext(AuthContext);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/gear-board`)
      .then((res) => setPosts((res.data.posts || []).filter((p) => !p.sold).slice(0, 4)))
      .catch(() => {});
  }, []);

  const timeAgo = (iso) => {
    const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 60) return `לפני ${Math.max(1, mins)} דק׳`;
    if (mins < 1440) return `לפני ${Math.floor(mins / 60)} שע׳`;
    return `לפני ${Math.floor(mins / 1440)} ימים`;
  };

  const condBadge = (c) =>
    c === 'חדש' ? 'bg-emerald-500 text-white' : c === 'כמו חדש' ? 'bg-blue-500 text-white' : 'bg-amber-500 text-white';

  return (
    <div className="relative mt-12 overflow-hidden rounded-3xl shadow-2xl" data-testid="home-gear-section">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-emerald-50 to-white"></div>
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-blue-300/30 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-emerald-300/30 rounded-full blur-3xl"></div>

      <div className="relative z-10 p-6 sm:p-10">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-green-500 rounded-2xl flex items-center justify-center shadow-lg">
              <ShoppingBag className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-black text-gray-900">🛒 לוח ציוד יד שנייה/חדש</h2>
              <p className="text-gray-600 text-sm sm:text-base">קנו ומכרו ציוד טניס בקהילה — מחבטים, נעליים, גידים ועוד</p>
            </div>
          </div>
          <Link to="/gear-board" data-testid="home-gear-cta">
            <Button className="bg-gradient-to-l from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white rounded-full font-bold px-6 shadow-lg">
              לכל המודעות
              <ArrowLeft className="w-4 h-4 mr-2" />
            </Button>
          </Link>
        </div>

        {posts.length === 0 ? (
          <div className="mt-6 bg-white/70 backdrop-blur rounded-2xl p-6 text-center border border-emerald-100">
            <span className="text-4xl block mb-2">🛒</span>
            <p className="font-bold text-gray-700">עדיין אין מודעות — היו הראשונים למכור ציוד!</p>
            <Link to="/gear-board">
              <Button className="mt-3 rounded-full font-bold bg-gradient-to-l from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white" data-testid="home-gear-empty-cta">
                פרסמו מודעה ראשונה
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-6">
            {posts.map((p) => (
              <Link key={p.id} to="/gear-board" data-testid={`home-gear-card-${p.id}`}>
                <Card className="overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all h-full border-emerald-100">
                  <div className="relative h-28 sm:h-32 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                    {p.images && p.images[0] ? (
                      <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-4xl">{CAT_EMOJI[p.category] || '📦'}</span>
                    )}
                    {(p.views || 0) >= 10 && (
                      <Badge className="absolute top-2 left-2 bg-red-500 text-white text-[10px] animate-pulse" data-testid={`gear-hot-showcase-${p.id}`}>🔥 מבוקש</Badge>
                    )}
                    <Badge className={`absolute top-2 right-2 text-[10px] ${condBadge(p.condition)}`}>{p.condition}</Badge>
                  </div>
                  <CardContent className="p-2.5">
                    <div className="flex items-start justify-between gap-1">
                      <h3 className="font-bold text-gray-800 text-xs sm:text-sm leading-tight line-clamp-2">{p.title}</h3>
                      <span className="font-black text-emerald-600 text-sm whitespace-nowrap">{p.price === 0 ? 'חינם' : `₪${p.price}`}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <Avatar className="w-5 h-5 border border-emerald-200">
                        {p.user_avatar && <AvatarImage src={p.user_avatar} alt={p.user_name} />}
                        <AvatarFallback className="bg-emerald-100 text-emerald-700 text-[9px] font-bold">{(p.user_name || '?').charAt(0)}</AvatarFallback>
                      </Avatar>
                      <span className="text-[11px] font-bold text-gray-600 truncate">{p.user_name}</span>
                      <span className="text-[10px] text-gray-400 mr-auto whitespace-nowrap">{timeAgo(p.created_at)}</span>
                    </div>
                    {p.area && (
                      <div className="flex items-center gap-0.5 mt-1 text-[10px] text-gray-400">
                        <MapPin className="w-3 h-3" />{p.area}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
